import { useClientSize } from '@app/hooks';
import { useLockScroll } from '@app/hooks/useLockScroll';
import { useOnClickOutside } from '@app/hooks/useOnClickOutside';
import { selectors } from '@app/store';
import { useGetNotificationsQuery } from '@app/store/api/notifications';
import { useAppSelector } from '@app/store/hooks';
import { BreakpointsEnum } from '@app/types';
import Modal from '@app/ui/components/Modal';
import NotificationBtnWithCounter from '@app/ui/components/NotificationBtnWithCounter';
import { useToggle } from '@b2b/shared/hooks';
import { FC, useCallback, useRef } from 'react';
import styled from 'styled-components';
import NotificationsDropdown from './notificationsDropdown';

interface Props {
  className?: string;
}

const Notifications: FC<Props> = ({ className }) => {
  const rootRef = useRef(null);
  useGetNotificationsQuery({ limit: 1, page: 1 });
  const { lockScroll, unlockScroll } = useLockScroll();

  const { getIsBreakpoint } = useClientSize();
  const isMobile = getIsBreakpoint('sm');

  const { state: isOpenNotifications, close: closeNotifications, toggle: toggleNotifications } = useToggle();

  const notViewedCount = useAppSelector(selectors.notifications.selectUnviewedNotifications);

  const toggleOpenNotificationsModal = useCallback(() => {
    if (isMobile) {
      isOpenNotifications ? unlockScroll() : lockScroll();
    }

    toggleNotifications();
  }, [isMobile, isOpenNotifications]);

  useOnClickOutside(rootRef, () => !isMobile && closeNotifications());

  return (
    <>
      <Root
        className={className}
        ref={rootRef}
      >
        <NotificationBtnWithCounter
          notViewedCount={notViewedCount}
          onClick={toggleOpenNotificationsModal}
        />
        {!isMobile && isOpenNotifications && <StyledNotificationsDropdown onClose={toggleOpenNotificationsModal} />}
      </Root>
      <StyledModal
        isOpen={isMobile && isOpenNotifications}
        onClose={toggleOpenNotificationsModal}
        withoutHeader
      >
        <StyledNotificationsDropdown onClose={toggleOpenNotificationsModal} />
      </StyledModal>
    </>
  );
};

const Root = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const StyledNotificationsDropdown = styled(NotificationsDropdown)`
  z-index: 10;
  @media (min-width: ${BreakpointsEnum.sm}px) {
    position: absolute;
    top: calc(100% + 20px);
    right: -50px;
  }
  @media (max-width: ${BreakpointsEnum.sm}px) {
    height: 100%;
  }
`;

const StyledModal = styled(Modal)`
  && {
    max-height: none;
    border-radius: 0;
    padding: 0;
    height: 100%;

    & > button {
      display: none;
    }
  }
`;

export default Notifications;
